import { useCallback, useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import {
  getTournament,
  getTtarTournamentSync,
  listTables,
  listTournamentParticipants,
  publishTournament,
  cancelTournament,
  retryTtarEnrollment,
  runTtarTournamentSync,
  ApiError,
} from '../../lib/api';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { formatClock, formatLocal } from '../../lib/time';
import { formatGel } from '../../lib/format';
import {
  feeGelFromMinor,
  formatRatingWindow,
  formatWarmup,
  PAYMENT_MODE_LABELS,
  tableNamesLabel,
  TTAR_RESULT_INPUT_MODE_LABELS,
  tournamentStatusColor,
  tournamentStatusLabel,
} from '../../lib/tournaments';
import type {
  AdminParticipant,
  AdminTournament,
  TableEmbed,
  TtarTournamentSyncResponse,
} from '../../lib/types';
import { TournamentRoster } from './TournamentRoster';
import { TournamentStream } from './TournamentStream';
import { TournamentTtarSync } from './TournamentTtarSync';

type TournamentTab = 'overview' | 'ttar' | 'participants' | 'stream';

const TABS: Array<{ id: TournamentTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'ttar', label: 'TTAR' },
  { id: 'participants', label: 'Participants' },
  { id: 'stream', label: 'Stream' },
];

export function TournamentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();

  const tournamentId = id ? Number(id) : NaN;
  const validId = Number.isSafeInteger(tournamentId) && tournamentId > 0;
  const [tab, setTab] = useState<TournamentTab>('overview');
  const [tournament, setTournament] = useState<AdminTournament | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [retryingTtar, setRetryingTtar] = useState(false);
  const [retryingEnrollment, setRetryingEnrollment] = useState<string | null>(null);
  const [latestTtarSync, setLatestTtarSync] = useState<TtarTournamentSyncResponse | null>(null);

  const detailLoader = useCallback(
    (signal: AbortSignal) => validId
      ? getTournament(tournamentId, signal)
      : Promise.reject(new Error('Invalid tournament id.')),
    [tournamentId, validId],
  );
  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);
  const participantsLoader = useCallback(
    (signal: AbortSignal) => validId
      ? listTournamentParticipants(tournamentId, signal)
      : Promise.reject(new Error('Invalid tournament id.')),
    [tournamentId, validId],
  );
  const ttarSyncLoader = useCallback(
    (signal: AbortSignal) => validId
      ? getTtarTournamentSync(tournamentId, signal)
      : Promise.reject(new Error('Invalid tournament id.')),
    [tournamentId, validId],
  );

  const detail = useApi<AdminTournament>(detailLoader);
  const tables = useApi<TableEmbed[]>(tablesLoader);
  const participants = useApi<AdminParticipant[]>(participantsLoader);
  const ttarSync = useApi<TtarTournamentSyncResponse>(ttarSyncLoader);

  useEffect(() => {
    if (detail.data) setTournament(detail.data);
  }, [detail.data]);

  useEffect(() => {
    setLatestTtarSync(null);
    setTab('overview');
  }, [tournamentId]);

  if (!validId) {
    return <div style={{ ...T.ui, color: PS.danger }}>Invalid tournament id.</div>;
  }

  const effectiveTtarSync = latestTtarSync ?? ttarSync.data;
  const playerNames = Object.fromEntries(
    (participants.data ?? []).map((participant) => [
      participant.user_id,
      [participant.users.first_name, participant.users.last_name].filter(Boolean).join(' '),
    ]),
  );

  const handleTtarRetry = async () => {
    setRetryingTtar(true);
    try {
      const result = await runTtarTournamentSync(tournamentId);
      setLatestTtarSync(result);
      push('success', 'TTAR synchronization completed.');
    } catch (err) {
      // A failed response can still mean the API recorded the durable result.
      setLatestTtarSync(null);
      ttarSync.refetch();
      push('error', err instanceof ApiError ? err.message : 'TTAR synchronization failed');
    } finally {
      setRetryingTtar(false);
    }
  };

  const handleEnrollmentRetry = async (userId: string) => {
    setRetryingEnrollment(userId);
    try {
      const result = await retryTtarEnrollment(tournamentId, userId);
      setLatestTtarSync(result);
      const enrollment = result.enrollments.find((row) => row.user_id === userId);
      if (enrollment?.status === 'enrolled') {
        push('success', 'Player enrolled in TTAR.');
      } else {
        push('error', enrollment?.last_error ? `TTAR enrollment failed: ${enrollment.last_error}` : 'TTAR enrollment did not complete.');
      }
    } catch (err) {
      setLatestTtarSync(null);
      ttarSync.refetch();
      push('error', err instanceof ApiError ? err.message : 'TTAR enrollment retry failed');
    } finally {
      setRetryingEnrollment(null);
    }
  };

  const handlePublish = async () => {
    if (!tournament) return;
    setPublishing(true);
    try {
      setTournament(await publishTournament(tournament.id));
      push('success', 'Tournament published.');
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = TABS.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextTab = TABS[nextIndex];
    setTab(nextTab.id);
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabs?.[nextIndex]?.focus();
  };

  const handleCancel = async () => {
    if (!tournament) return;
    try {
      const result = await cancelTournament(tournament.id);
      setTournament(result.tournament);
      setCancelOpen(false);
      push(
        'success',
        `Tournament cancelled. ${result.removed_participants} participant${result.removed_participants === 1 ? '' : 's'} removed from the roster.`,
      );
      participants.refetch();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Cancel failed');
    }
  };

  const title = tournament ? tournament.translations.en?.title ?? tournament.title : 'Tournament';
  const tabId = `tournament-${tab}-tab`;
  const panelId = `tournament-${tab}-panel`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>{title}</h1>
        {tournament && <StatusBadge status={tournament.status} />}
        <Button variant="secondary" onClick={() => navigate('/tournaments')}>← Back</Button>
      </header>

      {detail.loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading tournament…</div>}
      {detail.error && <ErrorPanel message={detail.error.message} />}

      {tournament && (
        <>
          <div role="tablist" aria-label="Tournament sections" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {TABS.map((item, index) => (
              <Button
                key={item.id}
                type="button"
                role="tab"
                id={`tournament-${item.id}-tab`}
                aria-selected={tab === item.id}
                aria-controls={`tournament-${item.id}-panel`}
                tabIndex={tab === item.id ? 0 : -1}
                variant={tab === item.id ? 'primary' : 'secondary'}
                onClick={() => setTab(item.id)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                {item.label}
              </Button>
            ))}
          </div>

          <section role="tabpanel" id={panelId} aria-labelledby={tabId}>
            {tab === 'overview' && (
              <Overview
                tournament={tournament}
                tables={tables.data ?? []}
                tablesError={tables.error?.message}
                publishing={publishing}
                onEdit={() => navigate(`/tournaments/${tournament.id}/edit`)}
                onPublish={() => void handlePublish()}
                onCancel={() => setCancelOpen(true)}
              />
            )}

            {tab === 'ttar' && (
              <TournamentTtarSync
                result={effectiveTtarSync}
                loading={ttarSync.loading && latestTtarSync === null}
                error={latestTtarSync === null ? ttarSync.error : null}
                retrying={retryingTtar}
                onRetry={() => void handleTtarRetry()}
                onRefresh={ttarSync.refetch}
                playerNames={playerNames}
                retryingEnrollment={retryingEnrollment}
                onRetryEnrollment={(userId) => void handleEnrollmentRetry(userId)}
              />
            )}

            {tab === 'participants' && (
              <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <h2 style={{ ...T.subheading, margin: 0 }}>Participants ({(participants.data ?? []).length})</h2>
                {participants.error && <ErrorPanel message={participants.error.message} />}
                <TournamentRoster
                  tournamentId={tournament.id}
                  feeMinor={tournament.fee_minor}
                  participants={participants.data ?? []}
                  loading={participants.loading}
                  onChanged={() => participants.refetch()}
                  enrollments={effectiveTtarSync?.enrollments}
                  retryingTtar={retryingTtar}
                  onRetryTtar={() => void handleTtarRetry()}
                />
              </section>
            )}

            {tab === 'stream' && <TournamentStream tournamentId={tournament.id} />}
          </section>
        </>
      )}

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel this tournament?"
        message="Frees the blocked tables and clears the entire roster in one step. This cannot be undone."
        confirmLabel="Cancel tournament"
        destructive
        onConfirm={handleCancel}
        onCancel={() => setCancelOpen(false)}
      />
    </div>
  );
}

function Overview({
  tournament,
  tables,
  tablesError,
  publishing,
  onEdit,
  onPublish,
  onCancel,
}: {
  tournament: AdminTournament;
  tables: TableEmbed[];
  tablesError?: string;
  publishing: boolean;
  onEdit: () => void;
  onPublish: () => void;
  onCancel: () => void;
}) {
  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Overview</h2>
        <Button type="button" variant="secondary" onClick={onEdit}>Edit tournament</Button>
      </div>
      {tablesError && <ErrorPanel message={`Could not load table names: ${tablesError}`} />}
      <Row label="ID" value={`#${tournament.id}`} />
      <Row label="Date" value={tournament.date} />
      <Row label="Time" value={`${formatClock(tournament.start_time)}–${formatClock(tournament.end_time)}`} />
      <Row label="Warmup" value={formatWarmup(tournament.warmup_minutes, tournament.warmup_start_time)} />
      <Row label="Tables" value={tableNamesLabel(tournament.blocked_table_ids, tables)} />
      <Row label="Fee" value={formatGel(feeGelFromMinor(tournament.fee_minor))} />
      <Row label="Payment" value={tournament.fee_minor > 0 ? PAYMENT_MODE_LABELS[tournament.payment_mode] : 'Free entry'} />
      <Row label="Capacity" value={tournament.max_participants != null ? String(tournament.max_participants) : 'Unlimited'} />
      <Row label="Min participants (TTAR)" value={tournament.min_participants != null ? String(tournament.min_participants) : '2 (default)'} />
      <Row label="TTAR rating" value={formatRatingWindow(tournament.min_ttar_rating, tournament.max_ttar_rating)} />
      <Row label="TTAR results" value={TTAR_RESULT_INPUT_MODE_LABELS[tournament.ttar_result_input_mode]} />
      <Row label="Registration closes" value={tournament.registration_closes_at ? formatLocal(tournament.registration_closes_at, 'yyyy-MM-dd HH:mm') : 'No deadline'} />
      {tournament.image_url && <Row label="Image" value={<a href={tournament.image_url} target="_blank" rel="noreferrer" style={{ color: PS.linkBlue }}>{tournament.image_url}</a>} />}

      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: `1px dashed ${PS.borderLight}`, paddingTop: 12 }}>
        <Button onClick={onPublish} disabled={tournament.status !== 'draft' || publishing}>
          {publishing ? 'Publishing…' : 'Publish'}
        </Button>
        <Button variant="danger" onClick={onCancel} disabled={tournament.status === 'cancelled' || tournament.status === 'completed'}>
          Cancel tournament
        </Button>
      </div>
      {tournament.status !== 'draft' && (
        <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
          Only a draft can be published — the button above is disabled as a hint; the API is the final word on the status transition either way.
        </p>
      )}
    </section>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div style={{ padding: 12, background: PS.errorBg, border: `1px solid ${PS.errorBorder}`, color: PS.errorText, borderRadius: 3 }} role="alert">
      {message}
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <div style={{ ...T.label, width: 160, flexShrink: 0 }}>{label}</div>
      <div style={{ ...T.ui, color: PS.nearBlack, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: AdminTournament['status'] }) {
  const color = tournamentStatusColor(status);
  return (
    <span style={{ ...T.microLabel, color, background: PS.white, border: `1px solid ${color}`, borderRadius: 3, padding: '2px 8px' }}>
      {tournamentStatusLabel(status)}
    </span>
  );
}

const cardStyle = {
  background: PS.white,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 4,
  padding: 16,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 8,
};
